import _get from "lodash/get";
import * as moment from "moment";
import { formatPatent, isNullOrUndefined } from "../../../shared";

const reverseProgressStatus = (progress) => {
  switch (progress) {
    case 0:
      return "PENDIENTE";
    case 15:
      return "COMPRANDO_CERTIFICADOS";
    case 30:
      return "INFORMACION_CANCELACIONES";
    case 45:
      return "MULTAS";

    case 55:
      return "INFORMACION_FLOTAS";
    case 65:
      return "INFORMACION_REMATES";
    case 70:
      return "INFORMACION_RECALLS";
    case 80:
      return "GENERANDO_INFORME";

    case 100:
      return "TERMINADO";

    default:
      return null;
  }
};

export const getProgressFromStatus = (status = "PENDIENTE") => {
  switch (status) {
    case "PENDIENTE":
      return 0;

    case "COMPRANDO_CERTIFICADOS":
    case "EXTRAYENDO_INFORMACION_CERT":
      return 15;

    case "INFORMACION_REVISIONES":
      return 25;

    case "INFORMACION_MULTAS_NO_PAGADAS":
      return 30;

    case "INFORMACION_PASADAS_PENDIENTES":
      return 40;

    case "INFORMACION_VIAS_EXCLUSIVAS":
      return 45;

    case "INFORMACION_FLOTAS":
      return 55;

    case "INFORMACION_REMATES":
      return 65;

    case "INFORMACION_RECALLS":
      return 70;

    case "INFORMACION_CONSUMO_EMISIONES":
      return 75;

    case "INFORMACION_PERMISO_CIRCULACION":
      return 80;

    case "GENERANDO_INFORME":
      return 90;

    case "TERMINADO":
      return 100;

    case "FALLIDO_COMPRA_CAV":
      return -2;

    case "FALLIDO_EXTRAC_CAV":
      return -3;

    case "FALLIDO_COMPRA_MLT":
      return -4;

    case "FALLIDO_EXTRAC_MLT":
      return -5;

    case "FALLIDO_GENERACION_REPORTE":
      return -6;

    case "FALLIDO":
      return -1;

    default:
      return 0;
  }
};

export const getMessageFromStatus = (status = "PENDIENTE") => {
  switch (status) {
    case "PENDIENTE":
      return "Pendiente.";

    case "COMPRANDO_CERTIFICADOS":
    case "EXTRAYENDO_INFORMACION_CERT":
      return "Obteniendo datos vehiculares.";

    case "INFORMACION_REVISIONES":
      return "Obteniendo datos de revisiones técnicas.";

    case "INFORMACION_MULTAS_NO_PAGADAS":
      return "Obteniendo datos de multas de MLT.";

    case "INFORMACION_PASADAS_PENDIENTES":
      return "Obteniendo datos de pasadas pendientes de pago.";

    case "INFORMACION_VIAS_EXCLUSIVAS":
      return "Obteniendo datos de infracciones en vías exclusivas y por restricción vehicular.";

    case "INFORMACION_FLOTAS":
      return "Obteniendo datos de flota de transporte público.";

    case "INFORMACION_REMATES":
      return "Obteniendo datos de remates.";

    case "INFORMACION_RECALLS":
      return "Obteniendo datos de recalls.";

    case "INFORMACION_CONSUMO_EMISIONES":
      return "Obteniendo datos de consumo y emisiones.";

    case "INFORMACION_PERMISO_CIRCULACION":
      return "Obteniendo datos de permiso de circulación.";

    case "GENERANDO_INFORME":
      return "Generando reporte.";

    case "FALLIDO":
      return "Ha ocurrido un error.";

    case "FALLIDO_COMPRA_CAV":
      return "Ha ocurrido un error en la compra del CAV.";

    case "FALLIDO_EXTRAC_CAV":
      return "Ha ocurrido un error en la extracción de datos del CAV.";

    case "FALLIDO_COMPRA_MLT":
      return "Ha ocurrido un error en la compra del MLT.";

    case "FALLIDO_EXTRAC_MLT":
      return "Ha ocurrido un error en la extracción de datos del MLT.";

    case "FALLIDO_GENERACION_REPORTE":
      return "Ha ocurrido un error en la generación del reporte.";

    default:
      return null;
  }
};

const reportPayloadMapper = (payload) => {
  const {
    unique_id,
    document_id,
    patent: patente,
    tipo_reporte,
    comprobante_uid,
    creator,
    paid,
    emission_date: emitDate,
    process_status: status = "FALLIDO",
    status_message: statusMsg,
    inserted_at: createdAt,
    cav_uid,
    mlt_uid,
    retry_failure: retryFailure = false,
  } = payload;

  return {
    unique_id,
    document_id,
    patente: formatPatent(patente),
    tipo_reporte,
    comprobante_uid,
    paid,
    creator,
    emitDate: emitDate && moment(emitDate).format("DD/MM/YYYY HH:mm"),
    status,
    statusMsg: getMessageFromStatus(status),
    createdAt: +moment(createdAt),
    cav_uid,
    mlt_uid,
    retryFailure,
  };
};

export const reportsDataMapper = (response) => {
  const parsed = {};
  const collection = _get(response, "response.search.hits.hits", []);

  collection.forEach((v) => {
    const data = v._source;

    if (data) {
      const {
        unique_id,
        document_id,
        patente,
        emitDate,
        tipo_reporte,
        creator,
        paid,
        status = "FALLIDO",
        createdAt,
        cav_uid,
        mlt_uid,
        retryFailure = false,
        comprobante_uid,
      } = reportPayloadMapper(data);

      parsed[unique_id] = {
        unique_id,
        document_id,
        emitDate,
        tipo_reporte,
        creator,
        paid,
        createdAt,
        statusMsg: getMessageFromStatus(status),
        status,
        progress: getProgressFromStatus(status),
        cav_uid,
        mlt_uid,
        retryFailure,
        comprobante_uid,
      };

      if (!isNullOrUndefined(patente)) {
        parsed[unique_id]["patente"] = patente;
      }
    }
  });

  return parsed;
};

export const reportsTotalMapper = (response) => {
  return _get(response, "response.search.hits.total", 0);
};

export const reportUpdateMapper = (streamMessage) => {
  const {
    status,
    fecha: eventDate,
    report_type: tipo_reporte,
    comprobante_uid,
    username,
    paid,
    unique_id_exp: unique_id,
    document_unique_id: document_id,
    progress,
    emission_date: emitDate,
    patent: patente,
    cav_uid,
    mlt_uid,
    retry_failure: retryFailure = false,
  } = streamMessage;

  const payload = {
    status,
    statusMsg: getMessageFromStatus(status),
    unique_id,
    document_id,
    paid,
    tipo_reporte,
    comprobante_uid,
    creator: username,
    cav_uid,
    mlt_uid,
    progress: progress || getProgressFromStatus(status),
    eventDate,
    retryFailure,
  };

  if (!isNullOrUndefined(patente)) {
    payload["patente"] = formatPatent(patente);
  }

  if (!isNullOrUndefined(emitDate)) {
    payload["emitDate"] = moment(emitDate).format("DD/MM/YYYY HH:mm");
  }

  return payload;
};
